import { type FormEvent, useId, useRef, useState } from 'react';

import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import {
  Flex,
  FormGroup,
  FormSelect,
  FormTextarea,
} from '@spotify-internal/encore-web';
import { DialogComposite } from '@spotify/confidence-core-react';
import {
  ObservationsV1IssueCloseExplanationReason as ExplanationReason,
  ObservationsV1IssueCloseReason,
  getUserFacingErrorMessage,
} from '@spotify/confidence-graphql';
import {
  IssueFragment,
  getIssueCloseReasonLabel,
  useCloseIssue,
} from '@spotify/plugin-confidence-issues-react';

import { getClosureReasonOptions } from './closureReasons';

type IssueCloseDialogProps = {
  issue: Pick<IssueFragment, 'name'>;
  closeReason: ObservationsV1IssueCloseReason;
  onClose: () => void;
  onClosed?: (closeReason: ObservationsV1IssueCloseReason) => void;
};

export const IssueCloseDialog = ({
  issue,
  closeReason,
  onClose,
  onClosed,
}: IssueCloseDialogProps) => {
  const alert = useApi(alertApiRef);
  const reasonId = useId();
  const detailsId = useId();
  const [reason, setReason] = useState<ExplanationReason | ''>('');
  const [details, setDetails] = useState('');
  const submitting = useRef(false);
  const dismissed = useRef(false);
  const { closeIssue, isMutating } = useCloseIssue();
  const label = getIssueCloseReasonLabel(closeReason);
  const reasonOptions = getClosureReasonOptions(closeReason);
  const canSubmit =
    Boolean(reason) &&
    (reason !== ExplanationReason.Other || Boolean(details.trim()));

  const submit = async (): Promise<void> => {
    if (!canSubmit || !reason || submitting.current) return;
    submitting.current = true;
    try {
      await closeIssue(issue.name, closeReason, {
        reason,
        details: details.trim(),
      });
    } catch (error: unknown) {
      if (dismissed.current) {
        alert.post({
          severity: 'error',
          message: getUserFacingErrorMessage(
            error,
            error instanceof Error
              ? error.message
              : 'Could not close the issue. Please try again.',
          ),
        });
      }
      throw error;
    } finally {
      submitting.current = false;
    }
    onClosed?.(closeReason);
    if (!dismissed.current) onClose();
  };

  return (
    <DialogComposite.Root
      open
      width="sm"
      onClose={() => {
        dismissed.current = true;
        onClose();
      }}
    >
      <DialogComposite.Content
        component="form"
        isLoading={isMutating}
        onSubmit={async (event: FormEvent<HTMLElement>) => {
          event.preventDefault();
          await submit();
        }}
       data-track-source-anchor="e7206d03a6;issue-close-dialog-content">
        <DialogComposite.Header>
          <DialogComposite.Title>Close issue: {label}</DialogComposite.Title>
        </DialogComposite.Header>
        <DialogComposite.Body>
          <Flex direction="column" gap="base">
            <FormGroup label="Reason" labelFor={reasonId}>
              <FormSelect
                id={reasonId}
                value={reason}
                disabled={isMutating}
                required
                onChange={event =>
                  setReason(
                    Object.values(ExplanationReason).find(
                      value =>
                        value === event.currentTarget.value &&
                        reasonOptions[value],
                    ) ?? '',
                  )
                }
               data-track-source-anchor="1be4696444;issue-close-dialog-form-select">
                <option value="" disabled>
                  Select a reason
                </option>
                {Object.entries(reasonOptions).map(([value, option]) => (
                  <option key={value} value={value}>
                    {option.label}
                  </option>
                ))}
              </FormSelect>
            </FormGroup>
            <FormGroup
              label={
                reason === ExplanationReason.Other
                  ? 'Explanation'
                  : 'Details (optional)'
              }
              labelFor={detailsId}
            >
              <FormTextarea
                id={detailsId}
                value={details}
                disabled={isMutating}
                required={reason === ExplanationReason.Other}
                onChange={event => setDetails(event.currentTarget.value)}
               data-track-source-anchor="63dfaa3323;issue-close-dialog-form-textarea"/>
            </FormGroup>
            <DialogComposite.SubmitError />
          </Flex>
        </DialogComposite.Body>
        <DialogComposite.Actions>
          <DialogComposite.Actions.Cancel />
          <DialogComposite.Actions.Submit disabled={!canSubmit}>
            Close issue
          </DialogComposite.Actions.Submit>
        </DialogComposite.Actions>
      </DialogComposite.Content>
    </DialogComposite.Root>
  );
};
